import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { DateRangePresetKey, DateRangeValue } from './interface';

dayjs.extend(utc);
dayjs.extend(customParseFormat);

export const DATE_RANGE_PRESETS: { key: DateRangePresetKey; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'lastWeek', label: 'Last week' },
  { key: 'custom', label: 'Custom range' },
];

export const DATE_INPUT_FORMAT = 'DD/MM/YYYY';
const DISPLAY_DATE_FORMAT = 'DD MMM YYYY';
const DISPLAY_TIME_FORMAT = 'hh:mm A';

/** Returns the range for a preset. "Last week" is the last 7 days including today. */
export const getPresetRange = (key: Exclude<DateRangePresetKey, 'custom'>, now: Dayjs = dayjs()): DateRangeValue => {
  const today = now.startOf('day');
  switch (key) {
    case 'yesterday': {
      const yesterday = today.subtract(1, 'day');
      return { start: yesterday, end: yesterday };
    }
    case 'lastWeek':
      return { start: today.subtract(6, 'day'), end: today };
    case 'today':
    default:
      return { start: today, end: today };
  }
};

/** Converts a local day range to the UTC ISO strings expected by the API (whole days, inclusive). */
export const toApiRange = ({ start, end }: DateRangeValue) => ({
  startTime: start.startOf('day').utc().format(),
  endTime: end.endOf('day').utc().format(),
});

export const formatRangeLabel = ({ start, end }: DateRangeValue): string =>
  start.isSame(end, 'day')
    ? start.format(DISPLAY_DATE_FORMAT)
    : `${start.format(DISPLAY_DATE_FORMAT)} - ${end.format(DISPLAY_DATE_FORMAT)}`;

/** Strictly parses a typed DD/MM/YYYY date. Returns null if malformed or in the future. */
export const parseDateInput = (text: string, now: Dayjs = dayjs()): Dayjs | null => {
  const parsed = dayjs(text.trim(), DATE_INPUT_FORMAT, true);
  return parsed.isValid() && !parsed.isAfter(now, 'day') ? parsed.startOf('day') : null;
};

export const formatDateInput = (date: Dayjs): string => date.format(DATE_INPUT_FORMAT);

export const formatMeetingDate = (startTime: string): string => dayjs(startTime).format(DISPLAY_DATE_FORMAT);

export const formatMeetingTime = (startTime: string, endTime: string): string =>
  `${dayjs(startTime).format(DISPLAY_TIME_FORMAT)} - ${dayjs(endTime).format(DISPLAY_TIME_FORMAT)}`;

/** Formats seconds as readable text, e.g. 876 -> "14 min 36 sec", 3900 -> "1 hr 5 min". Missing values show "-". */
export const formatSecondsAsText = (totalSeconds?: number | null): string => {
  if (totalSeconds === null || totalSeconds === undefined || Number.isNaN(totalSeconds)) return '-';
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  if (hours > 0) return minutes > 0 ? `${hours} hr ${minutes} min` : `${hours} hr`;
  if (minutes > 0) return remainingSeconds > 0 ? `${minutes} min ${remainingSeconds} sec` : `${minutes} min`;
  return `${remainingSeconds} sec`;
};
